import React, { Component, Suspense } from 'reactn';
import { withStyles, Box, Grid, List, ListItem, Snackbar } from '@material-ui/core';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import jwt_decode from 'jwt-decode';
import { Navigate } from 'react-router-dom';
import styles from './management.style';
import { Communications, FacialAuth } from './';
import { ManagementService } from './management.service';
import { LOCATIONS, ROLES, setJanusToken, SELECTED_HOUSING_UNIT } from '../../constants';
import { withRouter } from '../../util/router-compatibility';
import { fetchJanusToken, getFeatures, getAvailableAssignedRoles } from '../../util/APIUtils';
import { Loader } from '@orijinworks/frontend-commons';
import LocationDetailsModal from '../Locations/locationDetails';
import { setLocation as selectLocation, setHousingUnit as selectHousingUnit } from '../../v2/core/store/.';
import { isPermitted, PermissionControl } from '../permission-control/permission-control';
import withUnleash from '../../v2/core/unleash/withUnleash';
import { UnleashService } from '../../v2/services';
import { Certificates } from '../../v2/modules/management/';
import UnleashProvider from '../../v2/core/unleash/unleashProvider';
import { Resume } from '../../v2/modules/management/resume';
import { LOCATION_NAME_REGEX, TABS, LOCATION_CACHE_KEY } from '../../v2/services/constants';
import { UserStorage } from '../../util/UserStorage';
import { USER_CONTEXT } from '../../constants';
import { createManagementSubtreeEtag } from './util';

const Settings = React.lazy(() => import('./settings'));
const Forms = React.lazy(() => import('./forms'));
const Users = React.lazy(() => import('../../v2/shared/components/users/users'));
const Staff = React.lazy(() => import('./staff'));
const EditLocation = React.lazy(() => import('../manage-locations/edit/editLocation'));
const ManageHousingUnits = React.lazy(() => import('../manage-locations/edit/manageHousingUnits'));
const FacialAuthenticationSettings = React.lazy(() => import('../manage-locations/edit/facialAuthenticationSettings'));
const FriendsAndFamilyMessaging = React.lazy(() => import('./friends-and-family-messaging'));

const TABS_MAPPING = {
  [TABS.USER]: 'Learners',
  [TABS.PATHS]: 'Path',
  [TABS.FORMS]: 'Forms',
  [TABS.SETTINGS]: 'Settings',
  [TABS.SERVICES]: 'Services',
  [TABS.STAFF]: 'Staff',
  [TABS.FA]: 'Biometric Authentication',
  [TABS.FNF]: 'Friends & Family Messaging',
  [TABS.COMMUNICATIONS]: 'Communications',
};

export class Management extends Component {
  managementService = new ManagementService();
  NO_LOCATION = -1;
  state = {
    etag: createManagementSubtreeEtag(),
    snackBar: {
      open: false,
      snackBarMsg: null,
    },
    activeTab: TABS.USER,
    changingLocation: false,
    location: {
      active: this.NO_LOCATION,
      collection: LOCATIONS,
      locationName: null,
    },
    locationFeatures: [],
    redirectTo: null,
  };
  componentDidMount() {
    if (this.props.isTestSuite) {
      return;
    }
    this.handleRouteChange();
  }

  componentDidUpdate(prevProps, prevState) {
    // SETTING ETAG SO THAT NEW
    // ROUTE IS REFLECTED AMONGST COMPONENTS
    if (prevProps.location !== this.props.location) {
      this.setState({
        etag: createManagementSubtreeEtag(),
      });
    }

    // Handle route parameter changes (including back button navigation)
    if (prevProps.match?.params?.location !== this.props.match?.params?.location ||
      prevProps.match?.params?.housingUnit !== this.props.match?.params?.housingUnit) {
      this.handleRouteChange();
    }

    // Also handle cases where the pathname changes (like /manage to /manage/location)
    if (prevProps.location?.pathname !== this.props.location?.pathname) {
      // Only handle if we don't have URL parameters (to avoid double handling)
      if (!this.props.match?.params?.location && !this.props.match?.params?.housingUnit) {
        this.handleRouteChange();
      }
    }

    // Handle location changes - prioritize housing unit if available
    const locationChanged = prevProps.selectedLocation !== this.props.selectedLocation;
    const housingUnitChanged = prevProps.selectedHousingUnit !== this.props.selectedHousingUnit;

    if (locationChanged || housingUnitChanged) {
      // If housing unit is available and either location or housing unit changed, use housing unit
      if (this.props.selectedHousingUnit && this.props.selectedLocation) {
        this.changeLocationHandler(this.props.selectedHousingUnit, 'hu');
      }
      // If housing unit is not available but location changed, use facility location
      else if (!this.props.selectedHousingUnit && this.props.selectedLocation && locationChanged) {
        this.changeLocationHandler(this.props.selectedLocation);
      }
      // If housing unit was cleared (changed to null/undefined), use facility location
      else if (!this.props.selectedHousingUnit && housingUnitChanged && this.props.selectedLocation) {
        this.changeLocationHandler(this.props.selectedLocation);
      }
    }
  }

  /**
   * Get default location from cache or global constants
   * @returns {object|null} Default location object or null if none available
   */
  getDefaultLocation = () => {
    try {

      // Try to get from user cache first
      const userStorage = new UserStorage(USER_CONTEXT.personId);
      const cachedLocation = userStorage.getItem(LOCATION_CACHE_KEY);
      if (cachedLocation) {
        const parsed = JSON.parse(cachedLocation);
        return parsed;
      }

      // Fallback to first available location from LOCATIONS
      if (LOCATIONS && LOCATIONS.length > 0) {
        // Find the first active facility location (locationTypeId === 3)
        const firstFacility = LOCATIONS.find(loc =>
          loc.locationTypeId === 3 && loc.active === true
        );
        if (firstFacility) {
          return firstFacility;
        }

        // If no facility found, return the first active location
        const firstActive = LOCATIONS.find(loc => loc.active === true);
        if (firstActive) {
          return firstActive;
        }
      }

    } catch (error) {
      console.error('Error getting default location:', error);
    }

    return null;
  };

  /**
   * /manage/:facility/:housingUnit — sync URL to location state when both resolve in LOCATIONS.
   * @returns {boolean} true when this branch handled navigation.
   */
  applyHousingUnitUrlRoute = (match) => {
    if (!match?.params?.housingUnit || !match?.params?.location) {
      return false;
    }
    const urlLocationName = match.params.location;
    const urlHousingUnitName = match.params.housingUnit;
    const foundFacility = LOCATIONS.find(
      (loc) => loc.locationName.replace(LOCATION_NAME_REGEX, '') === urlLocationName
    );
    const foundHousingUnit = LOCATIONS.find(
      (loc) =>
        loc.locationName.replace(LOCATION_NAME_REGEX, '') === urlHousingUnitName &&
        loc.parentLocationId === foundFacility?.locationId
    );
    if (!foundFacility || !foundHousingUnit) {
      return false;
    }
    if (this.state.redirectTo) {
      this.setState({ redirectTo: null });
    }
    this.updateLocationState(foundHousingUnit, 'hu');
    this.props.selectLocation(foundFacility);
    this.props.selectHousingUnit(foundHousingUnit);
    return true;
  };

  /**
   * /manage/:location (facility only) — sync URL to facility; clears housing unit in Redux.
   * @returns {boolean} true when this branch handled navigation.
   */
  applyFacilityUrlRoute = (match) => {
    if (!match?.params?.location || match?.params?.housingUnit) {
      return false;
    }
    const urlLocationName = match.params.location;
    const foundLocation = LOCATIONS.find(
      (loc) => loc.locationName.replace(LOCATION_NAME_REGEX, '') === urlLocationName
    );
    if (!foundLocation) {
      return false;
    }
    if (this.state.redirectTo) {
      this.setState({ redirectTo: null });
    }
    this.updateLocationState(foundLocation);
    this.props.selectLocation(foundLocation);
    this.props.selectHousingUnit(null);
    return true;
  };

  /**
   * Bare /manage — redirect to a default facility (and optional housing unit) when possible.
   * @returns {boolean} true when pathname was /manage without :location (handled or not).
   */
  applyBaseManageRoute = (location, match, selectedHousingUnit, selectedLocation) => {
    if (location.pathname !== '/manage' || match?.params?.location) {
      return false;
    }
    if (selectedHousingUnit && selectedLocation) {
      const facilityRoute = selectedLocation.locationName.replace(LOCATION_NAME_REGEX, '');
      const housingUnitRoute = selectedHousingUnit.locationName.replace(LOCATION_NAME_REGEX, '');
      this.setState({ redirectTo: `/manage/${facilityRoute}/${housingUnitRoute}` });
      return true;
    }
    const targetLocation = selectedLocation || this.getDefaultLocation();
    if (targetLocation) {
      const locationRoute = `/manage/${targetLocation.locationName.replace(LOCATION_NAME_REGEX, '')}`;
      this.setState({ redirectTo: locationRoute });
      this.props.selectLocation(targetLocation);
    } else {
      this.setGlobal({ hiddenBreadcrumbItems: [] });
    }
    return true;
  };

  /**
   * When Redux has a selection but URL is not /manage, align browser URL (e.g. deep link restore).
   */
  syncReduxSelectionToUrl = (selectedHousingUnit, selectedLocation, location) => {
    if (selectedHousingUnit && selectedLocation && location.pathname !== '/manage') {
      const expectedHousingUnitRoute = `/manage/${selectedLocation.locationName.replace(
        LOCATION_NAME_REGEX,
        ''
      )}/${selectedHousingUnit.locationName.replace(LOCATION_NAME_REGEX, '')}`;
      if (location.pathname !== expectedHousingUnitRoute) {
        this.changeLocationHandler(selectedHousingUnit, 'hu');
      }
      return;
    }
    if (selectedLocation && location.pathname !== '/manage') {
      const expectedFacilityRoute = `/manage/${selectedLocation.locationName.replace(LOCATION_NAME_REGEX, '')}`;
      if (location.pathname !== expectedFacilityRoute) {
        this.changeLocationHandler(selectedLocation);
      }
    }
  };

  handleRouteChange = () => {
    const { match, selectedHousingUnit, selectedLocation, location } = this.props;
    if (this.applyHousingUnitUrlRoute(match)) {
      return;
    }
    if (this.applyFacilityUrlRoute(match)) {
      return;
    }
    if (this.applyBaseManageRoute(location, match, selectedHousingUnit, selectedLocation)) {
      return;
    }
    this.syncReduxSelectionToUrl(selectedHousingUnit, selectedLocation, location);
  };

  /**
   * @param {Location} loc
   * @param {string} type
   * @returns
   */
  updateLocationState = async (loc, type = 'facility') => {
    if (this.state.changingLocation === true) {
      return;
    }

    this.setState({ changingLocation: true });

    // Update Redux store to keep location picker in sync
    this.props.selectLocation(loc);

    // Save to cache for future use (only for facilities, not housing units)
    if (type === 'facility') {
      try {
        const userStorage = new UserStorage(USER_CONTEXT.personId);
        userStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify({
          locationId: loc.locationId,
          locationName: loc.locationName,
          locationTypeId: loc.locationTypeId,
          active: loc.active
        }));
      } catch (error) {
        console.error('Error saving location to cache:', error);
      }
    }

    await this.updateGlobalStates(loc);

    this.setState(
      {
        location: {
          active: loc.locationId,
          collection: LOCATIONS,
          locationName: loc.locationName,
        },
        changingLocation: false,
      },
      async () => await this.getFacilityFeatures()
    );
  };

  isFeatureExist = (featureName) => {
    if (!Array.isArray(this.state.locationFeatures)) return false;
    const feature = this.state.locationFeatures.find((feature) => feature.featureName === featureName);
    if (feature) {
      return feature.status;
    }
    return false;
  };

  /**
   * @name extractFeaturesFromResponse
   * @param {any} response - The API response which can be in multiple formats
   * @returns {array} Array of location features
   * @desc Extracts location features from various response formats
   */
  extractFeaturesFromResponse = (response) => (response?.statusCode === 200 && response.locationFeatures) || [];

  getFacilityFeatures = async () => {
    const hasAppropriateRole = this.global.isSuperAdmin || this.global.isFacilityAdmin || this.global.isTeacher;

    if (!hasAppropriateRole) return;

    try {
      const response = await getFeatures(this.state.location.active);
      // Handle both response formats: direct array or wrapped in object
      const locationFeatures = this.extractFeaturesFromResponse(response);
      this.setState({ locationFeatures });
    } catch (error) {
      console.error('Error loading facility features:', error);
      // Set empty array on error to prevent UI issues
      this.setState({ locationFeatures: [] });
    }
  };

  getDefaultTab = (queryString) => {
    const params = new URLSearchParams(queryString);
    if (params.has('tab')) {
      return params.get('tab');
    }
    return null;
  };
  /**
   * @name toggleSnackBar
   * @param {string} msg
   * @desc Shows / hides snack bar with relevant msg.
   * @return {void}
   */
  toggleSnackBar = (msg, state = true) => {
    this.setState({
      snackBar: {
        open: state,
        snackBarMsg: msg,
      },
    });
  };

  setLandingTab = (defaultTab) => {
    let activeTab = null;
    if (defaultTab) {
      activeTab = parseInt(defaultTab);
    } else if (isPermitted(this.global.cbacConfigFlag, this.global.permissions, 'View_User_List')) {
      activeTab = TABS.USER;
    } else if (isPermitted(this.global.cbacConfigFlag, this.global.permissions, 'Access_Staff_List')) {
      activeTab = TABS.STAFF;
    } else if (isPermitted(this.global.cbacConfigFlag, this.global.permissions, 'View_Form_List')) {
      activeTab = TABS.FORMS;
    } else if (isPermitted(this.global.cbacConfigFlag, this.global.permissions, 'Access_Communications_Tab')) {
      activeTab = TABS.COMMUNICATIONS;
    } else if (isPermitted(this.global.cbacConfigFlag, this.global.permissions, 'Access_Location_Settings')) {
      activeTab = TABS.SETTINGS;
    } else {
      activeTab = TABS.SERVICES;
    }
    this.setState({
      activeTab: activeTab,
    });
    this.setGlobal({
      hiddenBreadcrumbItems: [TABS_MAPPING[activeTab]],
    });
  };
  /**
   * @name setActiveTab
   * @param {number} activeTab
   * @desc Changes active tab state.
   * @return {void}
   */
  setActiveTab = (activeTab) => {
    this.setGlobal({
      hiddenBreadcrumbItems: [TABS_MAPPING[activeTab]],
    });
    this.setState({
      activeTab,
    });
  };

  get isServicesAvailable() {
    if (this.props.isTestSuite) {
      return true;
    }
    return !this.global.isTeacher && this.global.isSuperAdmin;
  }

  /**
   * @param {Location} loc
   */
  updateGlobalStates = async (loc) => {

    if (!this.global.cbacConfigFlag) {
      this.setGlobal({ selectedLocation: loc.locationId });
      return;
    }

    const janusToken = await fetchJanusToken(loc.locationId);
    setJanusToken(janusToken.token || '');
    const decoded = jwt_decode(janusToken.token);
    const rolesTypes = Array.isArray(decoded.rolesTypes) ? decoded.rolesTypes : [];
    const jwtRoleIds = Array.isArray(decoded.roles)
      ? decoded.roles.map((id) => Number(id))
      : [];
    let isSystemAdmin = rolesTypes.includes(ROLES.SYSTEM_ADMIN);
    /** Janus rolesTypes are role_type labels, not roles_ref.role_name — System Admin may share a type with Facility Admin (CC-14114). Resolve via JWT roles[] (role ids) vs assignable roles list. */
    if (!isSystemAdmin && jwtRoleIds.length > 0) {
      const assignable = await getAvailableAssignedRoles(loc.locationId);
      const nameFrom = (r) => ((r && (r.displayName || r.roleName || r.roleDisplayName)) || '').trim();
      const systemAdminRow = (assignable || []).find((r) => nameFrom(r) === ROLES.SYSTEM_ADMIN);
      if (systemAdminRow != null && systemAdminRow.roleId != null) {
        const sid = Number(systemAdminRow.roleId);
        if (jwtRoleIds.some((id) => id === sid)) {
          isSystemAdmin = true;
        }
      }
    }
    await this.setGlobal({
      isSuperAdmin: rolesTypes.includes(ROLES.SUPER_ADMIN),
      isSystemAdmin,
      isAttorney: rolesTypes.includes(ROLES.ATTORNEY),
      isTeacher: rolesTypes.includes(ROLES.TEACHER),
      isFacilityAdmin: rolesTypes.includes(ROLES.STAFF),
      isMessagingOnly: rolesTypes.includes(ROLES.MESSAGING_ONLY),
      selectedLocation: loc.locationId 
    });

  }

  /**
   * @name changeLocationHandler
   * @param {Location} loc
   * @param {string} type
   * @desc Fires when location change handler is fired.
   * @return {void}
   */
  changeLocationHandler = async (loc, type = 'facility') => {
    if (this.state.changingLocation === true) {
      return;
    }

    this.setState({ changingLocation: true });
    const defaultTab = this.getDefaultTab(this.props.location.search);
    let parentLocationId = '';
    let parentLocationName = '';
    let route;
    if (type === 'hu') {
      if (SELECTED_HOUSING_UNIT) {
        parentLocationId = SELECTED_HOUSING_UNIT.parentLocationId;
      }
      parentLocationName = this.state.location.locationName;
      route = `/manage/${this.props.selectedLocation.locationName.replace(LOCATION_NAME_REGEX, '')}/${loc.locationName.replace(
        LOCATION_NAME_REGEX,
        ''
      )}`;
    } else {
      route = `/manage/${loc.locationName.replace(LOCATION_NAME_REGEX, '')}`;
    }
    // Use replace instead of push to avoid creating multiple history entries for location changes
    this.props.history.replace(route);
    await this.updateGlobalStates(loc);

    this.setState(
      {
        location: {
          ...this.state.location,
          active: loc.locationId,
          locationName: loc.locationName,
          parentLocationId,
          parentLocationName,
          type,
        },
        changingLocation: false,
      },
      async () => await this.getFacilityFeatures()
    );
    this.setLandingTab(defaultTab);
  };

  render() {
    const { classes } = this.props;

    // Handle redirect using Navigate component
    if (this.state.redirectTo) {
      // Clear redirectTo immediately to prevent infinite loops
      const redirectPath = this.state.redirectTo;
      this.setState({ redirectTo: null });
      return <Navigate to={redirectPath} replace />;
    }

    return (
      <Suspense fallback={<Loader isLoading={true} />}>
        <Grid container item component="div" className={classes.content}>
          <br />
          <Snackbar
            onClose={() => this.toggleSnackBar(null, false)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={this.state.snackBar.open}
            autoHideDuration={2000}
            message={this.state.snackBar.snackBarMsg}
          />
          {/* BEGIN: SECTION */}
          {this.state.location.active !== this.NO_LOCATION && (
            <Grid container item style={{ overflow: 'auto' }} xs={12}>
              <List className={classes.tabWrapper}>
                <Box component="div" display="flex" alignItems="flex-end" justifyContent="flex-end">
                  <PermissionControl permissionName="View_User_List">
                    <ListItem
                      id="user-tab-btn"
                      data-cy-name="user-tab-btn"
                      onClick={() => this.setActiveTab(TABS.USER)}
                      button
                      className={[classes.tabItem, this.state.activeTab === TABS.USER ? classes.activeTab : null].join(' ')}
                    >
                      LEARNERS
                    </ListItem>
                  </PermissionControl>
                  <PermissionControl
                    forceRestriction={
                      !this.global.cbacConfigFlag && !(this.global.isSuperAdmin || this.global.isFacilityAdmin)
                    }
                    permissionName="Access_Staff_List"
                  >
                    <ListItem
                      id="staff-tab-btn"
                      data-cy-name="staff-tab-btn"
                      onClick={() => this.setActiveTab(TABS.STAFF)}
                      button
                      className={[classes.tabItem, this.state.activeTab === TABS.STAFF ? classes.activeTab : null].join(' ')}
                    >
                      STAFF
                    </ListItem>
                  </PermissionControl>
                  <UnleashProvider
                    flagName={UnleashService.FLAGS.DISABLE_V1_MESSAGING}
                    fallbackComponent={
                      <PermissionControl
                        forceRestriction={
                          !this.global.cbacConfigFlag && !(this.global.isSuperAdmin || this.global.isFacilityAdmin)
                        }
                        permissionName="Access_Communications_Tab"
                      >
                        <ListItem
                          id="communication-tab-btn"
                          data-cy-name="paths-tab-btn"
                          onClick={() => {
                            this.setActiveTab(TABS.COMMUNICATIONS);
                          }}
                          button
                          className={[
                            classes.tabItem,
                            this.state.activeTab === TABS.COMMUNICATIONS ? classes.activeTab : null,
                          ].join(' ')}
                        >
                          COMMUNICATIONS
                        </ListItem>
                      </PermissionControl>
                    }
                  >
                    <Box display="none" />
                  </UnleashProvider>
                  <UnleashProvider flagName={UnleashService.FLAGS.SHOW_CERTIFICATE_IN_ADMIN_PORTAL}>
                    {(this.global.isSuperAdmin || this.global.isFacilityAdmin || this.global.isTeacher) && this.state.location.type !== 'hu' && (
                      <ListItem
                        id="certificates-tab-btn"
                        data-cy-name="certificate-tab-btn"
                        onClick={() => {
                          this.setActiveTab(TABS.CERTIFICATES);
                        }}
                        button
                        className={[
                          classes.tabItem,
                          this.state.activeTab === TABS.CERTIFICATES ? classes.activeTab : null,
                        ].join(' ')}
                      >
                        CERTIFICATES
                      </ListItem>
                    )}
                  </UnleashProvider>
                  <UnleashProvider flagName={UnleashService.FLAGS.RESUME_MODULE}>
                    {(this.global.isSuperAdmin || this.global.isFacilityAdmin || this.global.isTeacher) && this.isFeatureExist(this.managementService.ResumeBuilder) &&
                      this.state.location.type !== 'hu' && (
                        <ListItem
                          id="resume-tab-btn"
                          data-cy-name="resume-tab-btn"
                          onClick={() => {
                            this.setActiveTab(TABS.RESUME);
                          }}
                          button
                          className={[classes.tabItem, this.state.activeTab === TABS.RESUME ? classes.activeTab : null].join(
                            ' '
                          )}
                        >
                          RESUMES
                        </ListItem>
                      )}
                  </UnleashProvider>

                  {this.state.location.type !== 'hu' && (
                    <PermissionControl permissionName="View_Form_List">
                      <ListItem
                        id="forms-tab-btn"
                        data-cy-name="forms-tab-btn"
                        onClick={() => {
                          this.setActiveTab(TABS.FORMS);
                        }}
                        button
                        className={[classes.tabItem, this.state.activeTab === TABS.FORMS ? classes.activeTab : null].join(
                          ' '
                        )}
                      >
                        FORMS
                      </ListItem>
                    </PermissionControl>
                  )}
                  {this.isServicesAvailable && this.state.location.type !== 'hu' && (
                    <ListItem
                      id="services-tab-btn"
                      data-cy-name="services-tab-btn"
                      onClick={() => {
                        this.setActiveTab(TABS.SERVICES);
                      }}
                      button
                      className={[classes.tabItem, this.state.activeTab === TABS.SERVICES ? classes.activeTab : null].join(
                        ' '
                      )}
                    >
                      SERVICES
                    </ListItem>
                  )}
                  {!this.global.isTeacher &&
                    <PermissionControl permissionName="Access_Location_Settings">
                      <ListItem
                        id="settings-tab-btn"
                        data-cy-name="settings-tab-btn"
                        onClick={() => this.setActiveTab(TABS.SETTINGS)}
                        button
                        className={[classes.tabItem, this.state.activeTab === TABS.SETTINGS ? classes.activeTab : null].join(
                          ' '
                        )}
                      >
                        SETTINGS
                      </ListItem>
                    </PermissionControl>
                  }
                  {this.global.isSuperAdmin && (
                    <ListItem
                      id="fa-tab-btn"
                      data-cy-name="fa-tab-btn"
                      onClick={() => {
                        this.setActiveTab(TABS.FA);
                      }}
                      button
                      className={[classes.tabItem, this.state.activeTab === TABS.FA ? classes.activeTab : null].join(' ')}
                    >
                      BIOMETRIC AUTHENTICATION
                    </ListItem>
                  )}

                </Box>
              </List>
            </Grid>
          )}
        </Grid>
        {/* END: SECTION */}
        {/* BEGIN: SECTION */}
        {this.state.location.active !== this.NO_LOCATION && (
          <Grid container item className={classes.tabContent}>
            {/* BEGIN: SETTINGS */}
            {this.state.activeTab === TABS.SETTINGS && (
              <div style={{ padding: '1.5rem 70px', width: '100%' }}>
                <EditLocation selectedLocation={{ ...this.state.location, locationId: this.state.location.active }} />
                <PermissionControl permissionName="Access_Location_Settings">
                  <Settings showSnackBar={this.toggleSnackBar} facility {...this.state} generalSectionTitle="Access" />
                </PermissionControl>
                {this.global.isSuperAdmin && <FacialAuthenticationSettings locationId={this.state.location.active} />}
                {this.state.location.type !== 'hu' && (
                  <ManageHousingUnits
                    locationId={this.state.location.active}
                    locationName={this.state.location.locationName}
                    showSnackBar={this.toggleSnackBar}
                  />
                )}
              </div>
            )}
            {/* END: SETTINGS */}
            {this.state.activeTab === TABS.CERTIFICATES && <Certificates locationId={this.state.location.active} />}
            {this.state.activeTab === TABS.RESUME && <Resume locationId={this.state.location.active} />}
            {/* BEGIN: SERVICES */}
            {this.state.activeTab === TABS.SERVICES && <LocationDetailsModal inLine />}
            {/* END: SERVICES */}
            {this.state.activeTab === TABS.USER && (
              <PermissionControl permissionName="View_User_List">
                <Users
                  history={this.props.history}
                  location={this.state.location.active}
                  locationName={this.state.location.locationName}
                  locationMeta={this.state.location}
                />
              </PermissionControl>
            )}
            {/* BEGIN: FORMS */}
            {this.state.activeTab === TABS.FORMS && (
              <PermissionControl permissionName="View_Form_List">
                <Forms showSnackBar={this.toggleSnackBar} {...this.state} />
              </PermissionControl>
            )}
            {/* END: FORMS */}
            {/* BEGIN: COMMUNICATIONS */}
            {this.state.activeTab === TABS.COMMUNICATIONS && (
              <UnleashProvider
                flagName={UnleashService.FLAGS.DISABLE_V1_MESSAGING}
                fallbackComponent={
                  <PermissionControl
                    forceRestriction={!this.global.cbacConfigFlag && !(this.global.isSuperAdmin || this.global.isFacilityAdmin)}
                    permissionName="Access_Communications_Tab"
                  >
                    <Communications history={this.props.history} showSnackBar={this.toggleSnackBar} {...this.state} />
                  </PermissionControl>
                }
              >
                <Box display="none" />
              </UnleashProvider>
            )}
            {/* END: COMMUNICATIONS */}
            {/* BEGIN: STAFF */}
            {this.state.activeTab === TABS.STAFF && (
              <PermissionControl
                forceRestriction={!this.global.cbacConfigFlag && !(this.global.isSuperAdmin || this.global.isFacilityAdmin)}
                permissionName="Access_Staff_List"
              >
                <Staff showSnackBar={this.toggleSnackBar} {...this.state} history={this.props.history} />
              </PermissionControl>
            )}
            {/* END: STAFF */}
            {/* BEGIN: COMMUNICATIONS */}
            {this.state.activeTab === TABS.FA && <FacialAuth showSnackBar={this.toggleSnackBar} {...this.state} />}
            {/* END: COMMUNICATIONS */}

            {/* BEGIN: F&F MESSAGING */}
            {this.state.activeTab === TABS.FNF && (
              <PermissionControl permissionName="Access_Friendsandfamily_Tab">
                <FriendsAndFamilyMessaging {...this.state} locationId={this.state.location.active} />
              </PermissionControl>
            )}
            {/* END: F&F MESSAGING */}
          </Grid>
        )}

        {/* END: SECTION */}
      </Suspense>
    );
  }
}

Management.propTypes = {
  unleashProps: PropTypes.shape({
    getClient: PropTypes.func,
  }),
  classes: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  match: PropTypes.object,
  selectedLocation: PropTypes.object,
  selectedHousingUnit: PropTypes.object,
  selectLocation: PropTypes.func,
  selectHousingUnit: PropTypes.func,
  isTestSuite: PropTypes.bool,
};

const mapStateToProps = (state) => {
  return state.app;
};

const mapDispatchToProps = (dispatch) => {
  return {
    selectLocation: (location) => dispatch(selectLocation(location)),
    selectHousingUnit: (housingUnit) => dispatch(selectHousingUnit(housingUnit)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(withRouter(withUnleash(Management, UnleashService.FLAGS.RESUME_MODULE))));
